import { AxiosResponse, CancelToken } from 'axios';

import { SpreadPerformanceRequest } from '../types/dto-entities';
import {
  AuthToken,
  Security,
  SystemProfile,
  VolatilityAlert,
  VolatilityProfile,
  CandleStickBar,
  TrendLine,
  ContractDescription,
  ContractMarketData,
  FrontRunner,
  BootstrapData,
  MacroLink,
  MacroNote,
  HistoricalDataParams,
  MarketDataParams,
  OpenOrderReport,
  OptionChainParams,
  OptionContract,
  OrderState,
  PlaceOrderReport,
  QuickNote,
  JobScheduleInfo,
  TradingClass,
  TradingClassParams,
  PlaceOrderParams,
  RunStrategyRequest,
  PriceAlert,
  Trade,
  GreeksParams,
  OptionGreeks,
  RollOptionRequest,
  CloseOptionRequest,
  AtrRequest,
  AtrResponse,
  LossRangeRequest,
  BetaRequest,
  SpreadPoint,
  RegimeAnalytics,
  GefsCollectJob,
  GefsResults,
  NgPriceJob
} from '../types/entities';
import { BarSize, MarketPriceType } from '../types/enums';
import {
  Dashboard,
  RedlineLeg,
  RedlineLegEvent,
  RedlineStrategy,
  RedlineStrategyEvent,
  isRedlineStrategy,
  Strategy,
  Leg,
  HotLapLeg,
  isHotLapStrategy,
  HotLapStrategy,
  PreTradeIndicators,
  HotLapStrategyEvent,
  HotLapLegEvent,
  StrategyEvent,
  isSlipstreamStrategy,
  isApexStrategy,
  SlipstreamStrategy,
  ApexStrategy,
  isPitlaneStrategy,
  PitlaneStrategy,
  PitlaneLeg,
  isOvertakeStrategy,
  OvertakeStrategy,
  OvertakeLeg,
  isPursuitStrategy,
  PursuitStrategy,
  PursuitHistoryPoint,
  PursuitLeg,
  isChicaneStrategy,
  ChicaneStrategy,
  ChicaneLeg
} from '../types/strategy-entities';
import { Contract, ContractDetails } from '../types/tws-entities';

import axios from './axios-api';

export const checkBrokerConnection = (): Promise<AxiosResponse<string>> => {
  return axios.get<string>('/api/broker/check-connection');
};

export const loadDashboard = () => {
  return axios.get<Dashboard>('/api/broker/dashboard');
};

const MACRO_NOTE_ENDPOINT = '/api/macro-note';

export const fetchMacroNotes = () => {
  return axios.get<MacroNote[]>(MACRO_NOTE_ENDPOINT);
};

export const createMacroNote = (note: MacroNote) => {
  return axios.post(MACRO_NOTE_ENDPOINT, note);
};

export const updateMacroNote = (note: MacroNote) => {
  return axios.put(`${MACRO_NOTE_ENDPOINT}/${note.id}`, note);
};

export const removeMacroNote = (note: MacroNote) => {
  return axios.delete(`${MACRO_NOTE_ENDPOINT}/${note.id}`);
};

const MACRO_LINK_ENDPOINT = '/api/macro-link';

export const fetchMacroLinks = (symbol?: string | undefined) => {
  const params = symbol ? { symbol } : undefined;
  return axios.get<MacroLink[]>(MACRO_LINK_ENDPOINT, { params });
};

export const createMacroLink = (link: MacroLink) => {
  return axios.post(MACRO_LINK_ENDPOINT, link);
};

export const updateMacroLink = (link: MacroLink) => {
  return axios.put(`${MACRO_LINK_ENDPOINT}/${link.id}`, link);
};

export const removeMacroLink = (link: MacroLink) => {
  return axios.delete(`${MACRO_LINK_ENDPOINT}/${link.id}`);
};

const TREND_LINE_ENDPOINT = '/api/trend-line';

export const fetchTrendLines = (conId: number) => {
  const params = { conId };
  return axios.get<TrendLine[]>(TREND_LINE_ENDPOINT, { params });
};

export const fetchTrendLinesWithAlert = () => {
  return axios.get<TrendLine[]>(`${TREND_LINE_ENDPOINT}/alert`);
};

export const createTrendLine = (trendLine: TrendLine) => {
  return axios.post(TREND_LINE_ENDPOINT, trendLine);
};

export const updateTrendLine = (trendLine: TrendLine) => {
  return axios.put(`${TREND_LINE_ENDPOINT}/${trendLine.id}`, trendLine);
};

export const removeTrendLine = (trendLine: TrendLine) => {
  return axios.delete(`${TREND_LINE_ENDPOINT}/${trendLine.id}`);
};

export const fetchHistoricalData = (params: HistoricalDataParams) => {
  return axios.post<CandleStickBar[]>('/api/broker/historical-data', params);
};

export const getHistoricalVolatility = (params: HistoricalDataParams) => {
  return axios.post<number>('/api/broker/historical-data/historical-volatility-30d', params);
};

export const authUser = (username: string, password: string) => {
  const requestBody = { username, password };
  return axios.post<AuthToken>('/api/login', requestBody);
};

export const fetchMarketData = (contract: Contract) => {
  return axios.post<ContractMarketData>('/api/broker/market-data', contract);
};

export const fetchMarketPrice = (type: MarketPriceType, params: MarketDataParams) => {
  return axios.post<number>(`/api/broker/market-price/${type}`, params);
};

export const getImpliedVolatility = (params: MarketDataParams) => {
  return axios.post<number>('/api/broker/market-data/implied-volatility-30d', params);
};

const PLACE_ORDER_ENDPOINT = '/api/broker/order';

export const placeOrder = (args: PlaceOrderParams) => {
  return axios.post<PlaceOrderReport>(`${PLACE_ORDER_ENDPOINT}/place`, args);
};

export const checkOrderMargin = (args: PlaceOrderParams) => {
  return axios.post<OrderState>(`${PLACE_ORDER_ENDPOINT}/check-margin`, args);
};

const OPEN_ORDER_ENDPOINT = '/api/broker/open-order';

export const fetchOpenOrders = () => {
  return axios.get<OpenOrderReport[]>(OPEN_ORDER_ENDPOINT);
};

export const cancelOpenOrder = (report: OpenOrderReport) => {
  return axios.put<number>(`${OPEN_ORDER_ENDPOINT}/${report.orderId}`, report);
};

export const fetchOptionChain = (request: OptionChainParams) => {
  return axios.post<OptionContract[]>('/api/broker/option-chain', request);
};

const FRONT_RUNNER_ENDPOINT = '/api/front_runner';

export const fetchFrontRunners = () => {
  return axios.get<FrontRunner[]>(FRONT_RUNNER_ENDPOINT);
};

export const addFrontRunner = (fr: FrontRunner) => {
  return axios.post<FrontRunner>(FRONT_RUNNER_ENDPOINT, fr);
};

export const removeFrontRunner = (fr: FrontRunner) => {
  return axios.delete(`${FRONT_RUNNER_ENDPOINT}/${fr.id}`);
};

const SCHEDULE_ENDPOINT = '/api/schedule';

export const isScheduleRunning = (): Promise<AxiosResponse<JobScheduleInfo[]>> => {
  return axios.get<JobScheduleInfo[]>(SCHEDULE_ENDPOINT);
};

export const toggleScheduleRunning = (): Promise<AxiosResponse<string>> => {
  return axios.post<string>('/api/schedule/toggle');
};

export const fetchSecurityContracts = (security: Security) => {
  return axios.post<ContractDetails[]>('/api/broker/security-contract', security);
};

export const fetchTradingClasses = (params: TradingClassParams) => {
  return axios.post<TradingClass[]>('/api/broker/trading-class', params);
};

const TRADE_ENDPOINT = '/api/trade';

export const getTradeHistory = (year: number, month: number, account: string | undefined) => {
  const params: { year: number; month: number; account?: string } = { year, month, account };
  return axios.get<Trade[]>(`${TRADE_ENDPOINT}/history`, { params });
};

export const getRecentTrades = () => {
  return axios.get<Trade[]>(`${TRADE_ENDPOINT}/recent`);
};

export const getStrategyTrades = (strategyId: string) => {
  return axios.get<Trade[]>(`${TRADE_ENDPOINT}/strategy/${strategyId}`);
};

export const updateTrade = (trade: Trade) => {
  return axios.put(`${TRADE_ENDPOINT}/${trade.id}`, trade);
};

const SECURITY_ENDPOINT = '/api/security';

export const fetchSecurities = () => {
  return axios.get<Security[]>(SECURITY_ENDPOINT);
};

export const createSecurity = (sec: Security) => {
  return axios.post(SECURITY_ENDPOINT, sec);
};

export const updateSecurity = (sec: Security) => {
  return axios.put(`${SECURITY_ENDPOINT}/${sec.id}`, sec);
};

export const removeSecurity = (sec: Security) => {
  return axios.delete(`${SECURITY_ENDPOINT}/${sec.id}`);
};

export const getBootstrapData = () => {
  return axios.get<BootstrapData>('/api/bootstrap');
};

export const searchContracts = (params: { query: string }) => {
  return axios.post<ContractDescription[]>('/api/contract-search', params);
};

const QUICK_NOTE_ENDPOINT = '/api/quick-note';

export const fetchQuickNotes = (symbol: string) => {
  return axios.get<QuickNote[]>(`${QUICK_NOTE_ENDPOINT}/${symbol}`);
};

export const createQuickNote = (quickNote: QuickNote) => {
  return axios.post(QUICK_NOTE_ENDPOINT, quickNote);
};

export const deleteQuickNote = (quickNote: QuickNote) => {
  return axios.delete(`${QUICK_NOTE_ENDPOINT}/${quickNote.id}`);
};

export const runStrategyTask = (strategyId: string, request: RunStrategyRequest) => {
  return axios.put<string | undefined>(`/api/run-strategy/${strategyId}`, request);
};

export const fetchSystemProfile = () => {
  return axios.get<SystemProfile>('/api/system-profile');
};

export const updateSystemProfile = (profile: SystemProfile) => {
  return axios.post('/api/system-profile', profile);
};

const STRATEGY_ENDPOINT = '/api/strategy';

export const createStrategy = (strategy: Strategy) => {
  if (isRedlineStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/redline`, strategy);
  } else if (isHotLapStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/hotlap`, strategy);
  } else if (isSlipstreamStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/slipstream`, strategy);
  } else if (isApexStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/apex`, strategy);
  } else if (isPitlaneStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/pitlane`, strategy);
  } else if (isOvertakeStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/overtake`, strategy);
  } else if (isPursuitStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/pursuit`, strategy);
  } else if (isChicaneStrategy(strategy)) {
    return axios.post(`${STRATEGY_ENDPOINT}/chicane`, strategy);
  }
  return axios.post(STRATEGY_ENDPOINT, strategy);
};

export const updateStrategy = (strategy: Strategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/${strategy.id}`, strategy);
};

export const deleteStrategy = (strategy: Strategy) => {
  return axios.delete(`${STRATEGY_ENDPOINT}/${strategy.id}`);
};

export const updateHotLapStrategy = (strategy: HotLapStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/hotlap/${strategy.id}`, strategy);
};

export const updateRedlineStrategy = (strategy: RedlineStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/redline/${strategy.id}`, strategy);
};

export const updateSlipstreamStrategy = (strategy: SlipstreamStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/slipstream/${strategy.id}`, strategy);
};

export const updateApexStrategy = (strategy: ApexStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/apex/${strategy.id}`, strategy);
};

export const updatePitlaneStrategy = (strategy: PitlaneStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/pitlane/${strategy.id}`, strategy);
};

export const updateOvertakeStrategy = (strategy: OvertakeStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/overtake/${strategy.id}`, strategy);
};

export const updatePursuitStrategy = (strategy: PursuitStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/pursuit/${strategy.id}`, strategy);
};

export const updateChicaneStrategy = (strategy: ChicaneStrategy) => {
  return axios.put(`${STRATEGY_ENDPOINT}/chicane/${strategy.id}`, strategy);
};

const STRATEGY_EVENT_ENDPOINT = '/api/strategy-event';

export const getLatestStrategyEvents = () => {
  return axios.get<StrategyEvent[]>(`${STRATEGY_EVENT_ENDPOINT}/latest`);
};

export const getStrategyEvents = (strategyId: string) => {
  return axios.get(`${STRATEGY_EVENT_ENDPOINT}/${strategyId}`);
};

export const compressRedlineStrategyLog = (strategyId: string, logs: RedlineStrategyEvent[]) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/redline/compress-log/${strategyId}`, logs);
};

export const clearRedlineStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/redline/clear-log/${strategyId}`);
};

export const compressHotLapStrategyLog = (strategyId: string, logs: HotLapStrategyEvent[]) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/hotlap/compress-log/${strategyId}`, logs);
};

export const clearHotLapStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/hotlap/clear-log/${strategyId}`);
};

export const clearSlipstreamStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/slipstream/clear-log/${strategyId}`);
};

export const clearApexStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/apex/clear-log/${strategyId}`);
};

export const clearPitlaneStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/pitlane/clear-log/${strategyId}`);
};

export const clearOvertakeStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/overtake/clear-log/${strategyId}`);
};

export const getPursuitHistory = (strategyId: string, barSize: BarSize, periods: number) => {
  return axios.post<PursuitHistoryPoint[]>(`${STRATEGY_ENDPOINT}/pursuit/${strategyId}/history`, { barSize, periods });
};

export const startPursuitCarRecording = (strategyId: string, delayMinutes: number) => {
  return axios.put<PursuitStrategy>(`${STRATEGY_ENDPOINT}/pursuit/${strategyId}/pursuit-car/start-recording`, { delayMinutes });
};

export const stopPursuitCarRecording = (strategyId: string) => {
  return axios.put<PursuitStrategy>(`${STRATEGY_ENDPOINT}/pursuit/${strategyId}/pursuit-car/stop-recording`);
};

export const clearPursuitStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/pursuit/clear-log/${strategyId}`);
};

export const clearChicaneStrategyLog = (strategyId: string) => {
  return axios.put(`${STRATEGY_EVENT_ENDPOINT}/chicane/clear-log/${strategyId}`);
};

const LEG_ENDPOINT = '/api/leg';

export const fetchActiveLegs = () => {
  return axios.get(LEG_ENDPOINT);
};

export const getAverageTrueRange = (request: AtrRequest) => {
  return axios.post<AtrResponse>(`${LEG_ENDPOINT}/average-true-range`, request);
};

export const calculateBeta = (request: BetaRequest) => {
  return axios.post<number>(`${LEG_ENDPOINT}/beta`, request);
};

export const getLossRange = (request: LossRangeRequest) => {
  return axios.post<number>(`${LEG_ENDPOINT}/loss-range`, request);
};

export const deleteLeg = (leg: Leg) => {
  return axios.delete(`${LEG_ENDPOINT}/${leg.id}`);
};

export const createRedlineLeg = (leg: RedlineLeg) => {
  return axios.post(`${LEG_ENDPOINT}/redline`, leg);
};

export const updateRedlineLeg = (leg: RedlineLeg) => {
  return axios.put(`${LEG_ENDPOINT}/redline/${leg.id}`, leg);
};

export const createHotLapLeg = (leg: HotLapLeg) => {
  return axios.post(`${LEG_ENDPOINT}/hot-lap`, leg);
};

export const updateHotLapLeg = (leg: HotLapLeg) => {
  return axios.put(`${LEG_ENDPOINT}/hot-lap/${leg.id}`, leg);
};

export const createPitlaneLeg = (leg: PitlaneLeg) => {
  return axios.post(`${LEG_ENDPOINT}/pitlane`, leg);
};

export const updatePitlaneLeg = (leg: PitlaneLeg) => {
  return axios.put(`${LEG_ENDPOINT}/pitlane/${leg.id}`, leg);
};

export const createChicaneLeg = (leg: ChicaneLeg) => {
  return axios.post(`${LEG_ENDPOINT}/chicane`, leg);
};

export const createOvertakeLeg = (leg: OvertakeLeg) => {
  return axios.post(`${LEG_ENDPOINT}/overtake`, leg);
};

export const updateOvertakeLeg = (leg: OvertakeLeg) => {
  return axios.put(`${LEG_ENDPOINT}/overtake/${leg.id}`, leg);
};

export const createPursuitLeg = (leg: PursuitLeg) => {
  return axios.post(`${LEG_ENDPOINT}/pursuit`, leg);
};

export const updatePursuitLeg = (leg: PursuitLeg) => {
  return axios.put(`${LEG_ENDPOINT}/pursuit/${leg.id}`, leg);
};

export const updateChicaneLeg = (leg: ChicaneLeg) => {
  return axios.put(`${LEG_ENDPOINT}/chicane/${leg.id}`, leg);
};

const LEG_EVENT_ENDPOINT = '/api/leg-event';

export const getLegEventsByStrategy = (strategyId: string) => {
  return axios.get(`${LEG_EVENT_ENDPOINT}/strategy/${strategyId}`);
};

export const clearLegEventsByStrategy = (strategyId: string) => {
  return axios.put(`${LEG_EVENT_ENDPOINT}/strategy/clear-log/${strategyId}`);
};

export const compressRedlineLegEventsByStrategy = (strategyId: string, logs: RedlineLegEvent[]) => {
  return axios.put(`${LEG_EVENT_ENDPOINT}/strategy/compress-log/redline/${strategyId}`, logs);
};

export const clearHotLapLegLog = (legId: string) => {
  return axios.put(`${LEG_EVENT_ENDPOINT}/clear-log/hot-lap/${legId}`);
};

export const compressHotLapLegLog = (legId: string, logs: HotLapLegEvent[]) => {
  return axios.put(`${LEG_EVENT_ENDPOINT}/compress-log/hot-lap/${legId}`, logs);
};

export const getSpreadPerformance = (params: SpreadPerformanceRequest) => {
  return axios.post<SpreadPoint[]>('/api/spread-performance', params);
};

export const getPreTradeIndicators = (strategyId: string) => {
  const params = { strategyId };
  return axios.get<PreTradeIndicators>('/api/spread/pretrade-indicators', { params });
};

export const fetchPriceAlerts = () => {
  return axios.get<PriceAlert[]>('/api/price-alert');
};

export const clearPriceAlerts = () => {
  return axios.post('/api/price-alert/clear');
};

export const runPriceAlertTask = () => {
  return axios.post<PriceAlert[]>('/api/price-alert');
};

export const getFedCalendarContent = (cancelToken: CancelToken, month?: string | undefined, year?: number | undefined) => {
  const params = { year, month, responseType: 'text', withCredentials: false, cancelToken, headers: { Accept: 'text/html' } };
  return axios.get<string>('/api/fed/month', { params });
};

export const getOptionGreeks = (params: GreeksParams) => {
  return axios.post<OptionGreeks>('/api/broker/option/greeks', params);
};

export const getAtmOptionLookup = (leg: Leg) => {
  return axios.post<ContractDetails>('/api/broker/option/atm', leg);
};

export const rollOptionContract = (args: RollOptionRequest) => {
  return axios.post<PlaceOrderReport>('/api/broker/option/roll', args);
};

export const closeOptionContract = (args: CloseOptionRequest) => {
  return axios.post<PlaceOrderReport>('/api/broker/option/close', args);
};

const VLT_PROFILE_ENDPOINT = '/api/volatility-profile';

export const fetchVolatilityProfiles = () => {
  return axios.get<VolatilityProfile[]>(VLT_PROFILE_ENDPOINT);
};

export const createVolatilityProfile = (profile: VolatilityProfile) => {
  return axios.post(VLT_PROFILE_ENDPOINT, profile);
};

export const updateVolatilityProfile = (profile: VolatilityProfile) => {
  return axios.put(`${VLT_PROFILE_ENDPOINT}/${profile.id}`, profile);
};

const VLT_ALERT_ENDPOINT = '/api/volatility-alert';

export const fetchVolatilityAlerts = () => {
  return axios.get<VolatilityAlert[]>(VLT_ALERT_ENDPOINT);
};

export const runVolatilityAlertTask = () => {
  return axios.post<VolatilityAlert[]>(VLT_ALERT_ENDPOINT);
};

export const runRegimeAnalyticsTask = () => {
  return axios.post<RegimeAnalytics>('api/regime-analytics');
};

const GEFS_COLLECT_ENDPOINT = '/api/backtest/gefs/collect';

export const startGefsCollect = (month: string) => {
  return axios.post<GefsCollectJob>(GEFS_COLLECT_ENDPOINT, null, { params: { month } });
};

export const fetchGefsCollectJob = (jobId: string) => {
  return axios.get<GefsCollectJob>(`${GEFS_COLLECT_ENDPOINT}/${jobId}`);
};

export const fetchCurrentGefsCollectJob = () => {
  return axios.get<GefsCollectJob | null>(`${GEFS_COLLECT_ENDPOINT}/current`);
};

const NG_PRICES_ENDPOINT = '/api/backtest/ng/prices';

export const startNgPriceFetch = (month: string) => {
  return axios.post<NgPriceJob>(NG_PRICES_ENDPOINT, null, { params: { month } });
};

export const fetchNgPriceJob = (jobId: string) => {
  return axios.get<NgPriceJob>(`${NG_PRICES_ENDPOINT}/${jobId}`);
};

export const fetchCurrentNgPriceJob = () => {
  return axios.get<NgPriceJob | null>(`${NG_PRICES_ENDPOINT}/current`);
};

export const dismissGefsCollectJob = (jobId: string) => {
  return axios.post(`${GEFS_COLLECT_ENDPOINT}/${jobId}/dismiss`);
};

export const dismissNgPriceJob = (jobId: string) => {
  return axios.post(`${NG_PRICES_ENDPOINT}/${jobId}/dismiss`);
};

export const fetchGefsResults = (month: string) => {
  return axios.get<GefsResults>('/api/backtest/gefs/results', { params: { month } });
};
